import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import palette from '../assets/sass/palette.scss';
import { useAuth } from '../contexts/AuthContext';
import ProfilePhotoManager from './ProfilePhotoManager';
import Logo from './Logo';
import { getDocs, query, collection, where, doc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useGameUtils } from '../utilities/GameUtils';
import { useLocation } from 'react-router-dom';
import { useWaglRating, useWaglLeaderboard } from '../hooks/useWaglRating';
import clubIcon from '../assets/images/icon-club-green.png';
import ProgressBar from './progressbar';

const ProfileSidebar = ({ showClose = false, handleClose = () => {} }) => {
  const { db } = useFirebase();
  const { userProfile } = useAuth();
  const {
    getAllGamesRecordsForUser,
    // eslint-disable-next-line no-unused-vars
    createCombinedInvitationsPlayers,
    fetchGameById,
    // eslint-disable-next-line no-unused-vars
    getAllUserDetailsByUserIds,
  } = useGameUtils();

  // Check if useParams is correctly providing the id
  const location = useLocation();
  const id = location.pathname.match(/^\/games\/([^/]+)/)?.[1];

  // eslint-disable-next-line no-unused-vars
  const [isOrganizerStatus, setIsOrganizerStatus] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [arrowDirection, setArrowDirection] = useState('neutral');
  const [gamesCount, setGamesCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [wins, setWins] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [losses, setLosses] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [gameData, setGameData] = useState(null);
  const [rankingData, setRankingData] = useState([]);
  const [playersPlayedWith, setPlayersPlayedWith] = useState(0);
  const [winLossPercentage, setWinLossPercentage] = useState(0);

  // Use new rating hooks
  const { 
    rating: ratingData, 
    // eslint-disable-next-line no-unused-vars
    loading: ratingLoading, 
    // eslint-disable-next-line no-unused-vars
    error: ratingError 
  } = useWaglRating(userProfile?.uid);

  const { 
    leaderboard, 
    // eslint-disable-next-line no-unused-vars
    loading: leaderboardLoading, 
    // eslint-disable-next-line no-unused-vars
    error: leaderboardError 
  } = useWaglLeaderboard();

  // Extract rating values with fallbacks
  const waglRating = ratingData?.waglRating || ratingData?.currentRating || 50;
  // eslint-disable-next-line no-unused-vars
  const waglRatingDelta = ratingData?.waglRatingDelta || 0;

  // Use refs to track mount state and cleanup
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    abortControllerRef.current = new AbortController();
    
    const getGamesCount = async () => {
      try {
        // Create a promise that rejects if aborted
        const gameStatsPromise = getAllGamesRecordsForUser(userProfile?.uid);
        const abortPromise = new Promise((_, reject) => {
          abortControllerRef.current.signal.addEventListener('abort', () => {
            reject(new Error('Request aborted'));
          });
        });
        
        const gameStats = await Promise.race([gameStatsPromise, abortPromise]);
        
        // Use setTimeout to defer state updates and avoid act warnings
        setTimeout(() => {
          if (isMountedRef.current && !abortControllerRef.current.signal.aborted) {
            // Use rating data for games count (includes all games played, not just organized)
            setGamesCount(gameStats?.gamesCount || 0);
            setWins(gameStats?.wins || 0);
            setLosses(gameStats?.losses || 0);
            setPlayersPlayedWith(gameStats?.playersPlayedWith || 0);
            setWinLossPercentage(gameStats?.winLossPercentage || 0);
          }
        }, 0);
      } catch (error) {
        if (error.message !== 'Request aborted') {
          console.error('Error getting games count:', error);
        }
        
        setTimeout(() => {
          if (isMountedRef.current && !abortControllerRef.current?.signal.aborted) {
            setGamesCount(0);
            setWins(0);
            setLosses(0);
            setPlayersPlayedWith(0);
            setWinLossPercentage(0);
          }
        }, 0);
      }
    };
    
    if (userProfile?.uid) {
      getGamesCount();
    }
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [userProfile?.uid, getAllGamesRecordsForUser, ratingData?.gamesPlayed]);

  const loadGame = useCallback(
    async (gameId, signal) => {
      if (!gameId) return;
      
      try {
        // Create a promise that rejects if aborted
        const gameDataPromise = fetchGameById(gameId);
        const abortPromise = new Promise((_, reject) => {
          signal?.addEventListener('abort', () => {
            reject(new Error('Request aborted'));
          });
        });
        
        const gameData = await Promise.race([gameDataPromise, abortPromise]);
        
        setTimeout(() => {
          if (isMountedRef.current && !signal?.aborted) {
            setGameData(gameData);
          }
        }, 0);
      } catch (error) {
        if (error.message !== 'Request aborted') {
          console.error('Error loading game:', error);
        }
        
        setTimeout(() => {
          if (isMountedRef.current && !signal?.aborted) {
            setGameData(null);
          }
        }, 0);
      }
    },
    [fetchGameById]
  );

  useEffect(() => {
    const controller = new AbortController();
    
    const loadGameData = async () => {
      if (id && !controller.signal.aborted) {
        await loadGame(id, controller.signal);
      }
    };
    
    loadGameData();
    
    return () => {
      controller.abort();
    };
  }, [id, loadGame]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    const getLatestWaglScoreRecord = async () => {
      const latestWaglScoreRecord = await getLatestWaglScoreRecord(userProfile?.uid);
      setArrowDirection(
        latestWaglScoreRecord?.latestScore > latestWaglScoreRecord?.previousScore
          ? 'up'
          : latestWaglScoreRecord?.latestScore === latestWaglScoreRecord?.previousScore
          ? 'neutral'
          : 'down'
      );
    };
    if (userProfile?.uid) {
      // getLatestWaglScoreRecord();
    }
  }, [userProfile?.uid]);

  // Update ranking data when leaderboard changes
  useEffect(() => {
    if (leaderboard && leaderboard.length > 0) {
      // Leaderboard data already includes rating deltas from the new API
      setRankingData(leaderboard);
    }
  }, [leaderboard]);

  const stats = [
    {
      title: 'Games',
      amt: gamesCount ? gamesCount : 0,
    },
    {
      title: 'Partners',
      amt: playersPlayedWith,
    },
    {
      title: 'Win %',
      amt: (winLossPercentage || 0)?.toFixed(0) || 0,
    },
  ];

  // const StatCell = ({ stat, index }) => {
  //   return (
  //     <Grid2
  //       size={4}
  //       sx={{ borderRight: index < stats.length - 1 ? `1px solid ${palette.darkSand}` : '0' }}
  //     >
  //       <Stack sx={{ alignItems: 'center' }}>
  //         <Typography
  //           component="strong"
  //           variant="small"
  //           sx={{ fontSize: '12px', color: palette.lightSand }}
  //         >
  //           {stat.title}
  //         </Typography>
  //         <Typography variant="stat" color="white">
  //           {stat.amt}
  //         </Typography>
  //       </Stack>
  //     </Grid2>
  //   );
  // };

  function createRankData(uid, direction, rank, name, rating) {
    const isMe = uid === userProfile?.uid;
    return {uid, direction, rank, name, rating, isMe };
  }

  const rankRows =
    rankingData?.length > 0
      ? rankingData.map((user, index) => {
          // For leaderboard, we don't have individual deltas, so default to neutral
          const delta = user.waglRatingDelta || 0;
          return createRankData(
            user.userId,
            delta > 0 ? 'up' : delta < 0 ? 'down' : 'up',
            index + 1,
            user.username,
            user?.waglRating?.toFixed(0) || user?.currentRating?.toFixed(0) || '50'
          );
        })
      : [createRankData('', 'up', 0, 'No data', 0)];

  const RankTable = ({ userProfile }) => {
    const getPlayerColour = (player) => {
      // const playerData = leaderboard?.find(p => p.uid === uid);
      if (player.isMe) {
        return palette.lime; //palette.primary;
      }
      return 'white';
    }

    const visibleRows = (() => {
      if (!rankRows || rankRows.length === 0) return [];
      const total = rankRows.length;
      const me = rankRows.find((r) => r.isMe);
      const myRank = me?.rank || 1;

      const windowSize = Math.min(5, total);
      let start = Math.max(1, myRank - Math.floor(windowSize / 2));
      let end = start + windowSize - 1;
      if (end > total) {
        end = total;
        start = Math.max(1, end - windowSize + 1);
      }

      const includeRanks = new Set();
      includeRanks.add(1);
      includeRanks.add(total);
      for (let r = start; r <= end; r++) includeRanks.add(r);

      return rankRows
        .filter((r) => includeRanks.has(r.rank))
        .sort((a, b) => a.rank - b.rank);
    })();

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>
              <Typography
                variant="small"
                component="strong"
                sx={{ fontSize: '12px', color: palette.lightSand }}
              >
                Rank
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                variant="small"
                component="strong"
                sx={{ fontSize: '12px', color: palette.lightSand }}
              >
                Name
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="small" color="primary" component="small">
                <Typography variant="factorA" component="span" className="flipped">
                  W
                </Typography>{' '}
                Rating
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, index) => (
            <>
            <TableRow key={`${row.name}-${row.rank}-${index}`} sx={{ borderTop: index === visibleRows.length - 1 ? `1px dashed ${palette.lightSand}` : 0 }}>
              <TableCell sx={{ width: 'auto' }}>
                <div />
                {/* {row.direction === 'up' && <img src={iconArrowUpGreen} alt="up" />} */}
                {/* {row.direction === 'down' && <img src={iconArrowDownRed} alt="down" />} */}
              </TableCell>
              <TableCell>
                <Typography variant={row.isMe ? 'strong' : 'emPurple'} conponent="span" color={ row.isMe ? palette.lime : palette.primary}>
                  {row.rank}
                </Typography>
              </TableCell>
              <TableCell>
                  <Typography color={getPlayerColour(row)} variant={row.isMe ? 'strong' : 'small'}>
                    {row.name} {row.isMe ? ' (You)' : ''}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography component="strong" variant={row.isMe ? 'strong' : 'small'} color={getPlayerColour(row)}>
                  {row.rating}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow key={`${row.name}-${row.rank}-${index}-progress`} sx={{ borderBottom: index === 0 ? `1px dashed ${palette.lightSand}` : 0 }}>
              <TableCell
                colSpan={4}
                sx={{
                  padding: 0,
                  // borderBottom: index === visibleRows.length - 1 ? `1px dashed ${palette.lightSand}` : 0,
                  // borderTop: index === visibleRows.length - 1 ? `1px dotted ${palette.lightSand}` : 0,
                }}
              >
                <ProgressBar
                  progress={row.rating}
                  useGradient={true}
                  gradientColours={['#FF6669', '#9B66FF', '#ABE832']}
                  trackColor="#FCFCFC"
                  sx={{ marginTop: '0px', marginBottom: '12px' }}
                />
              </TableCell>
            </TableRow>
            </>
          ))}
        </TableBody>
      </Table>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const ProfileSubhead = ({ icon, label, tag, align }) => {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: align ? align : 'center' }}
      >
        <img src={icon} alt={`${label} icon`} sx={{ color: palette.lightSand }} />
        <Typography
          gutterBottom
          component={tag ? tag : 'strong'}
          variant="small"
          color={palette.lightSand}
          sx={{ fontWeight: '600' }}
        >
          {label}
        </Typography>
      </Stack>
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    
    if (userProfile?.uid) {
      let games = [];
      const isOrganizer = async () => {
        try {
          // Check if request was aborted before making Firebase calls
          if (controller.signal.aborted) return;
          
          const userRef = doc(db, 'users', userProfile?.uid);
          const gamesRef = collection(db, 'games');
          const q = query(gamesRef, where('createdBy', '==', userRef));
          
          // Create a promise that rejects if aborted
          const queryPromise = getDocs(q);
          const abortPromise = new Promise((_, reject) => {
            controller.signal.addEventListener('abort', () => {
              reject(new Error('Request aborted'));
            });
          });
          
          const querySnapshot = await Promise.race([queryPromise, abortPromise]);

          if (querySnapshot && !controller.signal.aborted) {
            querySnapshot.forEach((doc) => {
              games.push(doc.data());
            });
          }
          
          // Use setTimeout to defer state update and avoid act warnings
          setTimeout(() => {
            if (isMountedRef.current && !controller.signal.aborted) {
              setIsOrganizerStatus(games.length > 0);
            }
          }, 0);
        } catch (error) {
          if (error.message !== 'Request aborted') {
            console.error('Error checking organizer status:', error);
          }
          
          setTimeout(() => {
            if (isMountedRef.current && !controller.signal.aborted) {
              setIsOrganizerStatus(false);
            }
          }, 0);
        }
      };
      isOrganizer();
    }
    
    return () => {
      controller.abort();
    };
  }, [userProfile?.uid, db]);

  // Global cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      {userProfile && (
        <Paper
          variant="profile"
          sx={{
            borderRadius: { xs: 0, md: '0 100px 0 0' },
            maxWidth: { xs: 'unset', md: '380px' },
          }}
        >
          {showClose && (
            <Box sx={{ textAlign: 'right' }}>
              <CloseIcon onClick={handleClose} sx={{ cursor: 'pointer' }} />
            </Box>
          )}
          <Logo invert={true} />
          <Box sx={{ mt: 6 }}>
            <ProfilePhotoManager />
          </Box>
          <Typography
            variant="factorExtraBold"
            component="h4"
            // gutterBottom
            color="white"
            sx={{
              // marginTop: '8px',
              fontSize: '28px',
              fontWeight: '800',
              textAlign: 'center',
            }}
          >
            {userProfile.firstName && userProfile.lastName
              ? `${userProfile.firstName} ${userProfile.lastName}`
              : userProfile.username || userProfile.email || userProfile.phone || 'Wagl User'}
          </Typography>
          {/* {isOrganizerStatus && <ProfileSubhead icon={iconMembership} label="Golf Organizer" />} */}
          {userProfile?.homeClub && (
            <Typography
              variant="small"
              color={palette.lime}
              sx={{ textAlign: 'center', pb: 2 }}
            >
              <img align="top" src={clubIcon} alt="Club" style={{ marginRight: '4px', width: '20px', height: '20px' }} />
              {userProfile?.homeClub}
            </Typography>
          )}
          <Stack direction="row" sx={{ width: '100%', margin: '0 auto' }}>
            <Stack
              variant="profileScores"
              sx={{ backgroundColor: '#CEB4FF', borderRadius: '32px 0 0 32px', width: '50%' }}
            >
              <Typography variant="stat" sx={{ display: 'block' }}>
                {waglRating?.toFixed(0) || '50'}
              </Typography>
              <Typography
                variant="sideBarStatsFunky"
                component="small"
                sx={{ display: 'block', fontSize: '10px' }}
              >
                <Typography variant="factorA" component="span" className="flipped">
                  W
                </Typography>
                agl
              </Typography>
            </Stack>
            <Stack
              variant="profileScores"
              sx={{ backgroundColor: '#FFFFFF', width: '50%' }}
            >
              <Typography variant="stat" sx={{ display: 'block' }}>
                {/* {waglRating?.toFixed(0) || '50'} */}
                {stats.find(stat => stat.title === 'Games')?.amt}
              </Typography>
              <Typography
                variant="sideBarStats"
                component="small"
                sx={{ display: 'block',fontSize: '10px' }}
              >
                GAMES
              </Typography>
            </Stack>
            <Stack
              variant="profileScores"
              sx={{ backgroundColor: '#C2F75A', borderRadius: '0 32px 32px 0', width: '50%' }}
            >
              <Typography variant="stat" sx={{ display: 'block' }}>
                {userProfile?.handicap || '??'}
              </Typography>
              <Typography
                variant="sideBarStatsFunky"
                component="small"
                sx={{ display: 'block', fontSize: '10px' }}
              >
                HCP
              </Typography>
            </Stack>
          </Stack>
          {/* <Grid2 container sx={{ my: 3, width: '100%' }}>
            {stats.map((stat, index) => {
              return <StatCell stat={stat} key={index} index={index} />;
            })}
          </Grid2> */}
          {leaderboardLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 4,
              minHeight: '200px',
            }}>
              <CircularProgress size={40} sx={{ color: 'white' }} />
            </Box>
          ) : (
            <Box sx={{ mt: 4 }}>
              <RankTable userProfile={userProfile} />
            </Box>
          )}
          {/* <Box sx={{ mt: 4, width: '100%' }}>
            <ProfileSubhead icon={iconTrophy} label="Best Round Ever:" tag="small" align="left" />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Avatar src="/placeholder.jpg" alt="" sx={{ width: '72px', height: '72px' }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500' }} color="white">
                  Pebble Beach Golf Links
                </Typography>
                <Typography variant="small" component="small" color={palette.lightSand}>
                  16th Nov 2025
                </Typography>
                <IconButton sx={{ float: 'right' }}>
                  <img src={iconArrowPurple} alt="arrow out" />
                </IconButton>
              </Box>
            </Stack>
          </Box> */}
        </Paper>
      )}
    </>
  );
};

export default ProfileSidebar;
